import { CustomButton } from '@ui-kit'
import {
  clearForceMsalPromptNextLogin,
  setForceMsalPromptNextLogin,
} from '@app-store'
import { allowMsalShellForThisTabAfterLogoutLogin } from '@ecrm-msal'
import { NAMESPACES } from '../utils/constants'
import {
  persist,
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../utils/agent.store'
import { useCallback, useEffect, useRef } from 'react'
import {
  clearSessionPostLogoutReturnPath,
  getSessionPostLogoutReturnPath,
  isSafeReturnPath,
  setSessionPostLogoutReturnPath,
} from '../utils/postLogoutReturnPath'

const FORCE_MSAL_PROMPT_QUERY = 'forceMsalPromptNextLogin'
const RETURN_PATH_QUERY = 'returnPath'

const LogoutPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const redirectedRef = useRef(false)
  const authProfileLoggedOut = useAppSelector(
    (state: RootState) => state.authUser.isAuthenticated === false
  )
  const sessionHasBackendAuth = useAppSelector((state: RootState) =>
    Boolean(state.session.isAuthenticated && state.session.token)
  )

  const navigateToResume = useCallback(() => {
    if (redirectedRef.current) return
    redirectedRef.current = true
    const path = getSessionPostLogoutReturnPath()
    clearSessionPostLogoutReturnPath()
    dispatch(clearForceMsalPromptNextLogin())
    const origin = window.location.origin
    window.location.href =
      path && isSafeReturnPath(path) ? `${origin}${path}` : origin
  }, [dispatch])

  const handleLoginClick = () => {
    allowMsalShellForThisTabAfterLogoutLogin()
    window.location.href = window.location.origin
  }

  /** Hydrate Redux + session from URL when session expiry wiped persist (query from handleSessionExpiry). */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const rp = params.get(RETURN_PATH_QUERY)
    if (rp && isSafeReturnPath(rp)) {
      setSessionPostLogoutReturnPath(rp)
    }
    if (params.get(FORCE_MSAL_PROMPT_QUERY) === '1') {
      dispatch(setForceMsalPromptNextLogin(true))
    }
    if (params.toString()) {
      void persist.flush().then(() => {
        const clean = new URL(window.location.href)
        clean.searchParams.delete(FORCE_MSAL_PROMPT_QUERY)
        clean.searchParams.delete(RETURN_PATH_QUERY)
        window.history.replaceState(
          {},
          '',
          clean.pathname + clean.search + clean.hash
        )
      })
    }
  }, [dispatch])

  useEffect(() => {
    if (authProfileLoggedOut) return
    if (!sessionHasBackendAuth) return
    navigateToResume()
  }, [authProfileLoggedOut, sessionHasBackendAuth, navigateToResume])

  return (
    <div className="flex items-center justify-center min-h-screen bg-accent-100">
      <div className="bg-accent-0 rounded-lg shadow-md p-6 max-w-md text-center">
        <h1 className="text-base font-medium text-accent-700">Logged Out</h1>
        <p className="mt-4 text-accent-600 font-light text-xs">
          You have been successfully logged out.
        </p>
        <p className="mt-2 text-accent-500 font-light text-xs">
          Thank you for using our application.
        </p>
        <div className="pt-4">
          <CustomButton
            type="button"
            isFilled={true}
            horizontalPaddingClasses="px-4.5"
            verticalPaddingClasses="py-2"
            onClick={handleLoginClick}
            dataQaId={'login-button-logout-screen'}
          >
            {NAMESPACES.LOGIN_BUTTON}
          </CustomButton>
        </div>
      </div>
    </div>
  )
}

export default LogoutPage
